import React, { useState } from 'react';
import axios from 'axios';
import { ItemSetCalculatorData } from '../types';
import HelpTip from './HelpTip';

interface BossFight {
    id: number;
    name: string;
    startTime: number;
    endTime: number;
    difficulty: number;
    type: 'boss' | 'trash';
    trashFightIds?: number[]; // For trash fights, contains the actual fight IDs
}

interface Druid {
    id: number;
    name: string;
    class: string;
}

const ItemSetCalculator: React.FC = () => {
    const [logUrl, setLogUrl] = useState('');
    const [bossFights, setBossFights] = useState<BossFight[]>([]);
    const [druids, setDruids] = useState<Druid[]>([]);
    const [selectedDruidId, setSelectedDruidId] = useState<number | null>(null);
    const [selectedFightIds, setSelectedFightIds] = useState<number[]>([]);
    const [hasMoonglow, setHasMoonglow] = useState(false);
    const [hasTranquilSpirit, setHasTranquilSpirit] = useState(false);
    const [result, setResult] = useState<ItemSetCalculatorData | null>(null);
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState<'url' | 'selection' | 'results'>('url');
    const [error, setError] = useState<string | null>(null);

    const handleUrlChange = async (url: string) => {
        setLogUrl(url);

        if (!url.trim()) {
            setBossFights([]);
            setDruids([]);
            setSelectedDruidId(null);
            setSelectedFightIds([]);
            setPhase('url');
            return;
        }

        // Auto-load fights and druids when URL is pasted
        setLoading(true);
        setError(null);
        setResult(null);
        setBossFights([]);
        setDruids([]);
        setSelectedDruidId(null);
        setSelectedFightIds([]);

        try {
            // Fetch both fights and druids in parallel
            const [fightsResponse, druidsResponse] = await Promise.all([
                axios.post('/api/attendance/get-boss-fights', {
                    logUrl: url,
                }),
                axios.post('/api/attendance/get-druids', {
                    logUrl: url,
                }),
            ]);

            setBossFights(fightsResponse.data.fights);
            setDruids(druidsResponse.data.druids);

            // Auto-select first druid if available
            if (druidsResponse.data.druids.length > 0) {
                setSelectedDruidId(druidsResponse.data.druids[0].id);
            }

            setPhase('selection');
        } catch (err: any) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.error || 'Failed to fetch data. Please check the URL and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFightSelection = (fightId: number, checked: boolean) => {
        if (checked) {
            setSelectedFightIds((prev) => [...prev, fightId]);
        } else {
            setSelectedFightIds((prev) => prev.filter((id) => id !== fightId));
        }
    };

    const handleSelectAll = () => {
        setSelectedFightIds(bossFights.map((fight) => fight.id));
    };

    const handleSelectNone = () => {
        setSelectedFightIds([]);
    };

    const handleProcessAnalysis = async () => {
        if (selectedFightIds.length === 0) {
            setError('Please select at least one fight to analyze');
            return;
        }

        if (selectedDruidId === null) {
            setError('Please select a druid for analysis');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            // Convert selected fight IDs to actual fight IDs
            // If trash fights are selected (id: -1), use the actual trash fight IDs
            const actualFightIds: number[] = [];

            selectedFightIds.forEach((selectedId) => {
                if (selectedId === -1) {
                    // This is the trash fights option
                    const trashFight = bossFights.find((f) => f.id === -1);
                    if (trashFight && trashFight.trashFightIds) {
                        actualFightIds.push(...trashFight.trashFightIds);
                    }
                } else {
                    // This is a regular boss fight
                    actualFightIds.push(selectedId);
                }
            });

            const response = await axios.post('/api/attendance/calculate-item-set-value', {
                logUrl,
                selectedFightIds: actualFightIds,
                selectedDruidId,
                hasMoonglow,
                hasTranquilSpirit,
            });

            setResult(response.data);
            setPhase('results');
        } catch (err: any) {
            console.error('Error analyzing log:', err);
            setError(err.response?.data?.error || 'Failed to analyze log. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setLogUrl('');
        setBossFights([]);
        setDruids([]);
        setSelectedDruidId(null);
        setSelectedFightIds([]);
        setResult(null);
        setError(null);
        setPhase('url');
    };

    const handleBackToSelection = () => {
        setPhase('selection');
        setResult(null);
    };

    const handleBackToUrl = () => {
        setPhase('url');
        setBossFights([]);
        setDruids([]);
        setSelectedDruidId(null);
        setSelectedFightIds([]);
        setResult(null);
    };

    const formatDuration = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const formatNumber = (num: number): string => {
        return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    return (
        <div className="item-set-calculator">
            {phase !== 'results' && (
                <>
                    <h2 className="heading-with-help">
                        4-Piece T3 Set Value
                        <HelpTip label="T3 set value" wide>
                            <h4>What this shows</h4>
                            <p>
                                How much MP5 the 4-piece T3 bonus is worth from this druid&apos;s casts on the selected
                                fights.
                            </p>
                            <p>
                                Tick Moonglow / Tranquil Spirit if those talents were specced — they change mana cost.
                            </p>
                        </HelpTip>
                    </h2>
                    <p className="analyzer-description">MP5 from 4-piece T3 on this log.</p>
                </>
            )}

            {phase === 'url' && (
                <div className="url-phase">
                    <div className="analyzer-form">
                        <div className="form-group">
                            <label htmlFor="logUrl">Warcraft Logs URL:</label>
                            <p className="analyzer-description">Paste a Warcraft Logs report URL.</p>
                            <input
                                type="url"
                                id="logUrl"
                                value={logUrl}
                                onChange={(e) => handleUrlChange(e.target.value)}
                                placeholder="https://fresh.warcraftlogs.com/reports/ABC123"
                                className="log-url-input"
                                required
                            />
                        </div>
                        {loading && <div className="loading-message">Loading boss fights...</div>}
                    </div>
                </div>
            )}

            {phase === 'selection' && (
                <div className="selection-phase">
                    <div className="phase-header">
                        <h3>Select Druid and Fights</h3>
                        <button onClick={handleBackToUrl} className="back-button">
                            ← Back to URL
                        </button>
                    </div>

                    <div className="druid-selection">
                        <h4>Select Druid for Analysis</h4>
                        <div className="druid-dropdown">
                            <select
                                value={selectedDruidId || ''}
                                onChange={(e) => setSelectedDruidId(parseInt(e.target.value) || null)}
                                className="druid-select"
                            >
                                <option value="">Select a druid...</option>
                                {druids.map((druid) => (
                                    <option key={druid.id} value={druid.id}>
                                        {druid.name} ({druid.class})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="talent-selection">
                        <h4 className="heading-with-help">
                            Druid Talents
                            <HelpTip label="Druid talents">
                                <p>These reduce mana cost, so they change the T3 set MP5 value.</p>
                                <ul>
                                    <li>
                                        <strong>Moonglow</strong> — 9% less mana on all spells
                                    </li>
                                    <li>
                                        <strong>Tranquil Spirit</strong> — 6% less mana on Healing Touch
                                    </li>
                                </ul>
                            </HelpTip>
                        </h4>
                        <p className="analyzer-description">Tick these if they were specced — they change mana cost.</p>
                        <div className="talent-toggles">
                            <label className="talent-toggle">
                                <input
                                    type="checkbox"
                                    checked={hasMoonglow}
                                    onChange={(e) => setHasMoonglow(e.target.checked)}
                                />
                                <span className="talent-name">Moonglow</span>
                            </label>
                            <label className="talent-toggle">
                                <input
                                    type="checkbox"
                                    checked={hasTranquilSpirit}
                                    onChange={(e) => setHasTranquilSpirit(e.target.checked)}
                                />
                                <span className="talent-name">Tranquil Spirit</span>
                            </label>
                        </div>
                    </div>

                    <div className="fight-selection-controls">
                        <button onClick={handleSelectAll} className="select-button">
                            Select All
                        </button>
                        <button onClick={handleSelectNone} className="select-button">
                            Select None
                        </button>
                        <span className="selection-count">
                            {selectedFightIds.length} of {bossFights.length} fights selected
                        </span>
                    </div>

                    <div className="fight-list">
                        {bossFights.map((fight) => (
                            <div key={fight.id} className="fight-item">
                                <label className="fight-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedFightIds.includes(fight.id)}
                                        onChange={(e) => handleFightSelection(fight.id, e.target.checked)}
                                    />
                                    <span className="fight-name">
                                        {fight.name}
                                        {fight.type === 'trash' && <span className="fight-type-badge">Trash</span>}
                                    </span>
                                    <span className="fight-difficulty">
                                        {fight.type === 'boss' ? `Difficulty: ${fight.difficulty}` : 'Trash Encounters'}
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={handleProcessAnalysis}
                        disabled={loading || selectedFightIds.length === 0}
                        className="process-button"
                    >
                        {loading ? 'Calculating...' : 'Calculate Item Set Value'}
                    </button>
                </div>
            )}

            {phase === 'results' && result && (
                <div className="results-phase">
                    <div className="phase-header">
                        <h3 className="heading-with-help">
                            T3 Set Value
                            <HelpTip label="T3 set value">
                                <p>
                                    MP5 from the 4-piece T3 bonus, based on this druid&apos;s mana spent on these
                                    fights.
                                </p>
                            </HelpTip>
                        </h3>
                        <button onClick={handleBackToSelection} className="back-button">
                            ← Back to Selection
                        </button>
                    </div>

                    {/* Featured MP5 Value */}
                    <div className="mp5-showcase">
                        <div className="mp5-card">
                            <div className="mp5-header">
                                <h3>🎉 T3 Set MP5 Value</h3>
                                <div className="mp5-value">
                                    <span className="mp5-number">{formatNumber(result.manaPer5)}</span>
                                    <span className="mp5-label">Mana per 5 seconds</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="results-summary">
                        <div className="summary-card">
                            <h4>Spell Usage Summary</h4>
                            <div className="summary-stats">
                                <div className="stat">
                                    <span className="stat-label">Selected Druid:</span>
                                    <span className="stat-value">
                                        {result.selectedDruid
                                            ? `${result.selectedDruid.name} (${result.selectedDruid.class})`
                                            : 'None'}
                                    </span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Talents:</span>
                                    <span className="stat-value">
                                        {result.hasMoonglow && result.hasTranquilSpirit
                                            ? 'Moonglow + Tranquil Spirit'
                                            : result.hasMoonglow
                                              ? 'Moonglow'
                                              : result.hasTranquilSpirit
                                                ? 'Tranquil Spirit'
                                                : 'None'}
                                    </span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Total Spell Casts:</span>
                                    <span className="stat-value">{formatNumber(result.totalSpellCasts)}</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Total Mana Cost:</span>
                                    <span className="stat-value">{formatNumber(result.totalManaCost)}</span>
                                </div>
                                <div className="stat">
                                    <span className="stat-label">Fight Duration:</span>
                                    <span className="stat-value">
                                        {formatDuration(result.totalFightDurationSeconds)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="spell-breakdown">
                        <h4>Spell Breakdown</h4>
                        <div className="breakdown-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Spell</th>
                                        <th>Rank</th>
                                        <th>Mana Cost</th>
                                        <th>Casts</th>
                                        <th>Total Mana</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.values(result.spellBreakdown)
                                        .sort((a, b) => b.totalMana - a.totalMana)
                                        .map((spell) => (
                                            <tr key={spell.spellId}>
                                                <td>{spell.spellName}</td>
                                                <td>{spell.rank}</td>
                                                <td>{formatNumber(spell.manaCost)}</td>
                                                <td>{formatNumber(spell.castCount)}</td>
                                                <td>{formatNumber(spell.totalMana)}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={handleClear} className="clear-button">
                        Clear and Start Over
                    </button>
                </div>
            )}
        </div>
    );
};

export default ItemSetCalculator;
